<script setup lang="ts">
const { t, locale } = useI18n();

const stemPrefix = computed(() => (locale.value === "fr" ? "alternatives/" : `${locale.value}/alternatives/`));
const { data } = await useAsyncData(
  `alternatives-${locale.value}`,
  () =>
    queryCollection("alternatives")
      .where("stem", "LIKE", `${stemPrefix.value}%`)
      .select("stem", "title", "description", "competitor", "seo")
      .all(),
  { watch: [locale] },
);

useSeoMeta({
  title: t('alternatives.seo.title'),
  description: t('alternatives.seo.description'),
});

 import.meta.prerender && defineOgImageComponent("Default", {
  title: t('alternatives.ogTitle'),
  description: t('alternatives.ogDescription'),
});
</script>

<template>
  <main class="relative">
    <div
      class="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
      aria-hidden="true"
    >
      <div
        class="to-primary-300 relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-blue-300 opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
        style="
          clip-path: polygon(
            74.1% 44.1%,
            100% 61.6%,
            97.5% 26.9%,
            85.5% 0.1%,
            80.7% 2%,
            72.5% 32.5%,
            60.2% 62.4%,
            52.4% 68.1%,
            47.5% 58.3%,
            45.2% 34.5%,
            27.5% 76.7%,
            0.1% 64.9%,
            17.9% 100%,
            27.6% 76.8%,
            76.1% 97.7%,
            74.1% 44.1%
          );
        "
      />
    </div>

    <div class="mx-auto max-w-7xl px-6 pt-24 sm:pt-32 lg:px-8">
      <div class="mx-auto max-w-2xl text-center">
        <h1 class="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl font-serif">
          {{ t('alternatives.pageTitle') }}
        </h1>
        <p class="mt-6 text-lg leading-8 text-gray-600">
          {{ t('alternatives.pageSubtitle') }}
        </p>
      </div>

      <div class="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-6 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
        <article
          v-for="item in data"
          :key="item.stem"
          class="flex flex-col items-start justify-between rounded-2xl bg-white p-8 ring-1 ring-gray-200 hover:ring-primary-500 transition-all"
        >
          <div class="flex items-center gap-x-4 text-xs">
            <span class="relative z-10 rounded-full bg-gray-100 px-3 py-1.5 font-medium text-gray-600">
              {{ t('alternatives.comparison') }}
            </span>
          </div>
          <div class="group relative">
            <h2 class="mt-3 text-lg font-semibold leading-6 text-gray-900 group-hover:text-primary-600">
              <NuxtLinkLocale :to="`/alternative/${item.stem?.replace(/^(.*\/)?alternatives\//, '')}`">
                <span class="absolute inset-0" />
                {{ t('alternatives.vsPrefix') }} {{ item.competitor }}
              </NuxtLinkLocale>
            </h2>
            <p class="mt-5 line-clamp-3 text-sm leading-6 text-gray-600">
              {{ item.seo?.description || item.description }}
            </p>
          </div>
        </article>
      </div>
    </div>

    <LandingEndCta />
  </main>
</template>
